import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchFolderById } from "./FoldersSlice";

import {
  createFileApi,
  getFilesByModeApi,
  getFileByIdApi,
  getTrashFilesApi,
  getStarredFilesApi,
  updateFileApi,
  uploadImageApi,
  uploadDocumentApi,
  uploadVideoApi,
  deleteAllFilesApi,
  softDeleteFileApi,
  deleteFilePermanentlyApi,
  deleteMultipleFilesApi,
  markFileAsStarredApi,
  unmarkFileAsStarredApi,
  restoreFileApi,
  shareMultipleFilesApi,
  getSharedFilesApi,
  removeSharedFileApi
} from "../Api/fileApi";

// -------------------------------
// STATE TYPE
// -------------------------------
interface FileState {
  sharedFiles: any[];
  files: any[];
  fileDetail: any | null;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
}

const initialState: FileState = {
  sharedFiles: [],
  files: [],
  fileDetail: null,
  loading: false,
  actionLoading: false,
  error: null,
};

// -------------------------------
// HELPERS
// -------------------------------
const removeFileFromState = (state: FileState, id: string) => {
  state.files = state.files.filter((f) => f._id !== id);
};

const updateFileInState = (state: FileState, updated: any) => {
  const fileId = updated._id || updated.id;
  if (!fileId) return;
  state.files = state.files.map((f) =>
    (f._id === fileId || f.id === fileId) ? { ...f, ...updated } : f
  );
};

// -------------------------------
// ASYNC THUNKS
// -------------------------------

// CREATE FILE
export const createFile = createAsyncThunk(
  "file/create",
  async (modify: any) => {
    const res = await createFileApi(modify);
    return res.data || res;
  }
);

// GET ALL FILES (BY MODE)
export const fetchFilesByMode = createAsyncThunk(
  "file/getAll",
  async (modeid: string) => {
    const res = await getFilesByModeApi(modeid);
    return res.data || res;
  }
);

// GET FILE BY ID
export const fetchFileById = createAsyncThunk(
  "file/getById",
  async (id: string) => {
    const res = await getFileByIdApi(id);
    return res.data || res;
  }
);

// GET TRASH FILES
export const fetchTrashFiles = createAsyncThunk(
  "file/getTrash",
  async (modeId: string) => {
    const res = await getTrashFilesApi(modeId);
    return res.data || res;
  }
);

// GET STARRED FILES
export const fetchStarredFiles = createAsyncThunk(
  "file/getStarred",
  async (modeId: string) => {
    const res = await getStarredFilesApi(modeId);
    return res.data || res;
  }
);

// UPDATE FILE
export const updateFile = createAsyncThunk(
  "file/update",
  async (payload: any) => {
    // If payload has _id but not id/modify, reformat it for the API
    const apiPayload = (payload.id && payload.modify)
      ? payload
      : { id: payload._id, modify: payload };

    const res = await updateFileApi(apiPayload);
    return res.data || res;
  }
);

// UPLOAD IMAGE
export const uploadImage = createAsyncThunk(
  "file/uploadImage",
  async (payload: any) => {
    const res = await uploadImageApi(payload);
    return res.data || res;
  }
);

// UPLOAD DOCUMENT
export const uploadDocument = createAsyncThunk(
  "file/uploadDocument",
  async (payload: any) => {
    const res = await uploadDocumentApi(payload);
    return res.data || res;
  }
);

// UPLOAD VIDEO
export const uploadVideo = createAsyncThunk(
  "file/uploadVideo",
  async (payload: any) => {
    const res = await uploadVideoApi(payload);
    return res.data || res;
  }
);

// DELETE ALL FILES (BY MODE)
export const deleteAllFiles = createAsyncThunk(
  "file/deleteAll",
  async (modeId: string) => {
    await deleteAllFilesApi(modeId);
    return modeId;
  }
);

// SOFT DELETE FILE
export const softDeleteFile = createAsyncThunk(
  "file/softDelete",
  async (id: string) => {
    await softDeleteFileApi(id);
    return id;
  }
);

// DELETE PERMANENTLY
export const deleteFilePermanently = createAsyncThunk(
  "file/deletePermanent",
  async (id: string) => {
    await deleteFilePermanentlyApi(id);
    return id;
  }
);

// DELETE MULTIPLE
export const deleteMultipleFiles = createAsyncThunk(
  "file/deleteMultiple",
  async (fileIds: string[]) => {
    await deleteMultipleFilesApi(fileIds);
    return fileIds;
  }
);

// SHARE MULTIPLE
export const shareMultipleFiles = createAsyncThunk(
  "file/shareMultiple",
  async (data: { fileIds: string[]; userIds: string[] }) => {
    const res = await shareMultipleFilesApi(data);
    return res.data || res;
  }
);

// STAR FILE
export const markFileAsStarred = createAsyncThunk(
  "file/star",
  async (id: string) => {
    const res = await markFileAsStarredApi(id);
    return res.data || res;
  }
);

// UNSTAR FILE
export const unmarkFileAsStarred = createAsyncThunk(
  "file/unstar",
  async (id: string) => {
    const res = await unmarkFileAsStarredApi(id);
    return res.data || res;
  }
);

// RESTORE FILE
export const restoreFile = createAsyncThunk(
  "file/restore",
  async (id: string) => {
    const res = await restoreFileApi(id);
    return res.data || res;
  }
);

// FETCH SHARED FILES
export const fetchSharedFiles = createAsyncThunk(
  "shared/fetchFiles",
  async ({ userId, page_no, page_size }: { userId: string; page_no?: number; page_size?: number }, { rejectWithValue }) => {
    try {
      const res = await getSharedFilesApi(userId, page_no, page_size);
      return res.data || res;
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch shared files");
    }
  }
);

// REMOVE SHARED FILE
export const removeSharedFile = createAsyncThunk(
  "shared/removeFile",
  async ({ fileId, userId }: { fileId: string; userId: string }, { rejectWithValue }) => {
    try {
      await removeSharedFileApi(fileId, userId);
      return fileId;
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to remove shared file");
    }
  }
);

// -------------------------------
// SLICE
// -------------------------------

const fileSlice = createSlice({
  name: "file",
  initialState,
  reducers: {
    clearFileDetail: (state) => {
      state.fileDetail = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(createFile.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createFile.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload) {
          state.files.unshift(action.payload);
        }
      })
      .addCase(createFile.rejected, (state) => {
        state.actionLoading = false;
      })

      // GET ALL FILES
      .addCase(fetchFilesByMode.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFilesByMode.fulfilled, (state, action) => {
        state.files = Array.isArray(action.payload) ? action.payload : (action.payload?.data || []);
        state.loading = false;
      })
      .addCase(fetchFilesByMode.rejected, (state) => {
        state.loading = false;
      })

      // GET BY ID
      .addCase(fetchFileById.fulfilled, (state, action) => {
        state.fileDetail = action.payload;
        state.loading = false;
      })

      // TRASH FILES
      .addCase(fetchTrashFiles.fulfilled, (state, action) => {
        state.files = Array.isArray(action.payload) ? action.payload : (action.payload?.data || []);
      })

      // STARRED FILES
      .addCase(fetchStarredFiles.fulfilled, (state, action) => {
        state.files = Array.isArray(action.payload) ? action.payload : (action.payload?.data || []);
      })

      // UPDATE FILE
      .addCase(updateFile.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload?.file || action.payload;
        if (updated?._id) {
          updateFileInState(state, updated);
          if (state.fileDetail && state.fileDetail._id === updated._id) {
            state.fileDetail = { ...state.fileDetail, ...updated };
          }
        }
      })

      // UPLOAD IMAGE/DOC/VIDEO (These typically update an existing entry or create a new one)
      .addCase(uploadImage.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(uploadImage.fulfilled, (state, action) => {
        state.actionLoading = false;
        const uploaded = action.payload?.data || action.payload;
        if (uploaded?._id) {
          // Check if it exists, if so update, else push
          const exists = state.files.find((f: any) => f._id === uploaded._id);
          if (exists) updateFileInState(state, uploaded);
          else state.files.unshift(uploaded);
        }
      })
      .addCase(uploadImage.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(uploadDocument.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.actionLoading = false;
        const uploaded = action.payload?.data || action.payload;
        if (uploaded?._id) {
          const exists = state.files.find((f: any) => f._id === uploaded._id);
          if (exists) updateFileInState(state, uploaded);
          else state.files.unshift(uploaded);
        }
      })
      .addCase(uploadDocument.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(uploadVideo.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(uploadVideo.fulfilled, (state, action) => {
        state.actionLoading = false;
        const uploaded = action.payload?.data || action.payload;
        if (uploaded?._id) {
          const exists = state.files.find((f: any) => f._id === uploaded._id);
          if (exists) updateFileInState(state, uploaded);
          else state.files.unshift(uploaded);
        }
      })
      .addCase(uploadVideo.rejected, (state) => {
        state.actionLoading = false;
      })

      // SOFT DELETE
      .addCase(softDeleteFile.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(softDeleteFile.fulfilled, (state, action) => {
        state.actionLoading = false;
        removeFileFromState(state, action.payload);
      })
      .addCase(softDeleteFile.rejected, (state) => {
        state.actionLoading = false;
      })

      // PERMANENT DELETE
      .addCase(deleteFilePermanently.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteFilePermanently.fulfilled, (state, action) => {
        state.actionLoading = false;
        removeFileFromState(state, action.payload);
      })
      .addCase(deleteFilePermanently.rejected, (state) => {
        state.actionLoading = false;
      })

      // DELETE MULTIPLE
      .addCase(deleteMultipleFiles.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteMultipleFiles.fulfilled, (state, action) => {
        state.actionLoading = false;
        const deletedIds = action.payload;
        state.files = state.files.filter((f) => !deletedIds.includes(f._id));
      })
      .addCase(deleteMultipleFiles.rejected, (state) => {
        state.actionLoading = false;
      })

      // STAR / UNSTAR
      .addCase(markFileAsStarred.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload?.file || action.payload;
        if (updated?._id || updated?.id) updateFileInState(state, updated);
      })
      .addCase(unmarkFileAsStarred.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload?.file || action.payload;
        if (updated?._id || updated?.id) updateFileInState(state, updated);
      })

      // RESTORE FILE
      .addCase(restoreFile.fulfilled, (state, action) => {
        // Typically restore removes from trash list
        removeFileFromState(state, action.payload?._id || action.payload);
      })
      // SYNC FROM FOLDER FETCH
      .addCase(fetchFolderById.fulfilled, (state, action) => {
        const folder = action.payload;
        if (folder?.file && Array.isArray(folder.file)) {
          state.files = folder.file;
        }
      })

      // FETCH FILES
      .addCase(fetchSharedFiles.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSharedFiles.fulfilled, (state, action) => {
        state.loading = false;
        state.sharedFiles = Array.isArray(action.payload) ? action.payload : (action.payload?.data || []);
      })
      .addCase(fetchSharedFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // REMOVE FILE
      .addCase(removeSharedFile.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(removeSharedFile.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.sharedFiles = state.sharedFiles.filter((f) => f._id !== action.payload);
      })
      .addCase(removeSharedFile.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearFileDetail } = fileSlice.actions;
export default fileSlice.reducer;
